import { parseUtcTimestampMs } from './timeUtils';

/**
 * Data age thresholds — operational, not normative. One source for every view that judges
 * how old a zone's input data is (dashboard card, monitoring banner, Netzmonitoring).
 *
 * Delayed/unavailable apply to the station measurement (last Lemonbeat / station-logger
 * message) and to the load-flow result: the measurement pool recalculates at least every
 * 2 min (POWERFLOW_FORCE_INTERVAL_MS), so both are expected to be minutes old at most.
 */
export const DATA_DELAYED_MINUTES = 5;
export const DATA_UNAVAILABLE_MINUTES = 30;

/**
 * Anchor thresholds of the load-flow input voltage — mirror the backend's model confidence
 * (ZoneStatisticsService: measured_anchor < 10 min, stale_anchor < 60 min, else nominal_anchor).
 * Change both sides together.
 */
export const ANCHOR_FRESH_MINUTES = 10;
export const ANCHOR_STALE_MINUTES = 60;

export type DataFreshness = 'fresh' | 'delayed' | 'unavailable' | 'none';

export const DATA_FRESHNESS_LABELS: Record<DataFreshness, string> = {
  fresh: 'aktuell',
  delayed: 'verzögert',
  unavailable: 'nicht verfügbar',
  none: 'keine Daten',
};

/** Age of a backend timestamp in ms, null when it is missing or unparsable. */
export function dataAgeMs(timestamp: string | null | undefined, nowMs: number = Date.now()): number | null {
  if (!timestamp) return null;
  const ms = parseUtcTimestampMs(timestamp);
  return Number.isNaN(ms) ? null : nowMs - ms;
}

export function freshnessOfAge(ageMs: number | null): DataFreshness {
  if (ageMs == null) return 'none';
  const ageMins = Math.floor(ageMs / 60_000);
  if (ageMins < DATA_DELAYED_MINUTES) return 'fresh';
  return ageMins < DATA_UNAVAILABLE_MINUTES ? 'delayed' : 'unavailable';
}

export function dataFreshness(timestamp: string | null | undefined, nowMs: number = Date.now()): DataFreshness {
  return freshnessOfAge(dataAgeMs(timestamp, nowMs));
}

/** "< 1 Min." · "12 Min." · "3 Std. 5 Min." */
export function formatAge(ageMs: number): string {
  const ageMins = Math.floor(ageMs / 60_000);
  if (ageMins < 1) return '< 1 Min.';
  if (ageMins < 60) return `${ageMins} Min.`;
  const hours = Math.floor(ageMins / 60);
  const mins = ageMins % 60;
  return mins > 0 ? `${hours} Std. ${mins} Min.` : `${hours} Std.`;
}
